import PropTypes from 'prop-types';
import { Fragment, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { initialize as formInitialize, reduxForm, change } from 'redux-form';
import { useEffect } from 'react';
import { styled } from '@mui/system';

import ContentContainer from '@libComponents/ContentContainer';
import { GridContainer, GridItem, Input } from '@formComponents';
import AutocompleteFetch from '@formComponents/AutocompleteFetch';
import AutocompleteBase from '@formComponents/Autocomplete';
import InputRadio from '@formComponents/InputRadio';
import { Button } from '@libComponents';
import { required } from '@libs/validationRules';

import { DOMAIN_NAME } from '../../leaveCategory/common';
import { FORM_NAME, IS_EMERGENCY } from '../common';
import TextArea from '@formComponents/TextArea';
import DatePickerBase from '@formComponents/DatePicker';
import { LEAVE_CATEGORY } from 'leaveCategory/redux.datasets';
import InputFile from '@formComponents/InputFile';

import { getEmplopyeeRemainingLeaves } from 'leaveAndHolidays/redux.actions';

// Libs
import { createLeave } from "../redux.actions";
import { setSnack } from 'utilities/redux.actions';
import { hasFormErrorSelector } from 'common/redux.selectors';
import dayjs from 'dayjs';
import { Alert, Box, Divider, Typography } from '@mui/material';
import { TableBody, TableRow } from '@mui/material';
import TableCellContainer from '@libComponents/table/TableCellContainer';
import TableContainer from '@libComponents/table/TableContainer';
import TableHeaderContainer from '@libComponents/table/TableHeaderContainer';

import { DOMAIN_NAME as COMMON_DOMAIN } from 'common/common';
import { GET_ALL_EMPLOYEE_NAME_EMAIL } from 'common/redux.datasets';
import { getAllEmployeeSelector } from 'common/redux.selectors';
import { LEAVE_TYPE } from 'leaveManagement/common';
import { scrollToTop } from '@libs/helpers';

const propTypes = {
  mode: PropTypes.oneOf(['create', 'update']),
};
const defaultProps = {
  mode: 'create',
};

/**
 * ReduxForm stepper rules
 * * Connect each step with reduxForm() to the same form name.
 * * Specify the destroyOnUnmount: false flag to preserve form data across form component unmounts.
 */
const ButtonWrapper = styled('div')({
  position: 'absolute',
  bottom: 0,
  right: '50%',
  transform: 'translate(50%, 50%)',
  zIndex: 4,
});

const StyledButton = styled(Button)(({ theme: { spacing, typography } }) => ({
  height: spacing(4.75),
  width: spacing(25),
  fontSize: typography.subtitle1.fontSize,
  marginLeft: spacing(0.75),
  marginRight: spacing(0.75),
}));

const StyledBox = styled(Box)(
  ({ theme: { spacing, palette, shape } }) => ({
    width: '100%',
    background: palette.background.paper,
    overflow: 'auto',
    borderRadius: shape.borderRadius,
    border: `1px solid ${palette.divider}`,
    '& table': {
      width: '100%',
      tableLayout: 'auto'
    }
  })
);

const TABLE_DATA = {
  'category': 'category',
  'remainingDays': 'remaining Days',
  'totalDays': "total Days",
};



const CreateLeave = reduxForm({
  form: FORM_NAME, // Form name
  destroyOnUnmount: false, // Preserve data
  onSubmit: (v) => v,
})(({ mode, handleSubmit: handleReduxFormSubmit, dispatch, ...rest }) => {
  const RXDispatch = useDispatch();
  const [data, setData] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [dropDownValue, setDropDownValue] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const navigate = useNavigate();
  const [leaveType, setLeaveType] = useState(0);
  const [isEmergency, setIsEmergency] = useState(false);
  const [currentDisplayYear, setCurrentDisplayYear] = useState(new Date().getFullYear());
  const [yearCrossError, setYearCrossError] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);

  useEffect(() => {
    RXDispatch(formInitialize(FORM_NAME, {
      isEmergency: isEmergency.toString(),
      leaveType: "0"
    }));
  }, [mode]);

  useEffect(() => {
    // Fetch leave data for current year on component mount
    fetchLeaveDataForYear(new Date().getFullYear(), true);
  }, []);

  const fetchLeaveDataForYear = (year, isInitial = false) => {
    RXDispatch(
      getEmplopyeeRemainingLeaves({
        payload: { selectedYear: year },
        handleSubmit: (data) => handleRemainingLeaves(data, isInitial)
      })
    );
  };

  const handleRemainingLeaves = (data, isInitial = false) => {
    if (data && data.length) {
      // Filter leaves where totalDays > 0
      const filteredData = data.filter(item => item.totalDays > 0);
      setData(filteredData);
      
      const categories = filteredData.map(item => item.category);
      setAvailableCategories(categories);
      setIsLoadingCategories(false);
    } else {
      setData([]);
      setAvailableCategories([]);
      setIsLoadingCategories(false);
    }
  };

  const handleStartDateChange = (value) => {
    if (!value) {
      setStartDate(null);
      setYearCrossError(false);
      return;
    }

    const selectedYear = dayjs(value).year();
    
    // Check if year is different from current display year
    if (selectedYear !== currentDisplayYear) {
      setCurrentDisplayYear(selectedYear);
      fetchLeaveDataForYear(selectedYear);
    }

    setStartDate(value);

    // Check if end date exists and is in different year
    if (endDate) {
      const endYear = dayjs(endDate).year();
      if (selectedYear !== endYear) {
        setYearCrossError(true);
        dispatch(change(FORM_NAME, 'endDate', null));
        setEndDate(null);
      } else {
        setYearCrossError(false);
        setTotalNumberOfDays(value, endDate, leaveType);
      }
    }
  };

  const handleEndDateChange = (value) => {
    if (!value) {
      setEndDate(null);
      setYearCrossError(false);
      return;
    }

    const endYear = dayjs(value).year();
    const startYear = dayjs(startDate).year();

    // Validate that end date is in the same year as start date
    if (startYear !== endYear) {
      setYearCrossError(true);
      // Reset end date
      dispatch(change(FORM_NAME, 'endDate', null));
      return;
    }

    setYearCrossError(false);
    setEndDate(value);
    setTotalNumberOfDays(startDate, value, leaveType);
  };

  const handleCreate = () => {
    if (hasError) {
      scrollToTop();
      handleReduxFormSubmit();
      return;
    }

    if (exceedsBalance) {
      scrollToTop();
      RXDispatch(setSnack({
        message: `You cannot apply for ${currentTotalDays} day(s) of leave as you only have ${selectedLeaveInfo?.remainingDays} day(s) remaining`,
        type: 'error'
      }));
      return;
    }

    if (mode === "create") {
      RXDispatch(createLeave({ handleSubmit }));
    }
  }

  const handleSubmit = (payload) => {
    RXDispatch(formInitialize(FORM_NAME, {
      isEmergency
    }));

    setTimeout(() => {
      if (payload?.response?.status !== 400) {
        navigate("/leave-&-holidays/all-leaves");
      }
    }, 1000)
  }

  const hasError = useSelector((state) =>
    hasFormErrorSelector(state, FORM_NAME)
  );

  const setTotalNumberOfDays = (startDate, endDate, leaveType) => {
    const totalNumberOfDays = dayjs(endDate).diff(dayjs(startDate), 'day') + 1 - leaveType;
    dispatch(change(FORM_NAME, 'totalNumberOfDays', totalNumberOfDays));
  }

  const inputFileHandler = () => {
    return dropDownValue && (dropDownValue.toLowerCase().includes("sick") || dropDownValue.toLowerCase().includes("paternity"))
  }

  const selectedLeaveInfo = data.find((item) => item.category.name === dropDownValue);
  const currentTotalDays = (startDate && endDate)
    ? Math.max(0, dayjs(endDate).diff(dayjs(startDate), 'day') + 1 - Number(leaveType || 0))
    : 0;
  const exceedsBalance = !!(
    selectedLeaveInfo &&
    selectedLeaveInfo.remainingDays !== undefined &&
    selectedLeaveInfo.remainingDays !== null &&
    selectedLeaveInfo.remainingDays >= 0 &&
    currentTotalDays > selectedLeaveInfo.remainingDays
  );

  let alertText;
  let severityType;
  if (dropDownValue) {
    switch (selectedLeaveInfo?.remainingDays) {
      case undefined:
        alertText = `You are not eligible for this leave`
        severityType = "error"
        break;
      case -1:
        alertText = `You are not applicable for ${dropDownValue ? selectedLeaveInfo?.category?.name : this}`
        severityType = "error"
        break;
      case 0:
        alertText = `Your are out of balance! You have 0 days remaining out of ${dropDownValue ? selectedLeaveInfo?.totalDays : 0}`
        severityType = "error"
        break;
      case null:
        alertText = `Your are out of balance!`
        severityType = "error"
        break;
      default:
        if (exceedsBalance) {
          alertText = `Cannot apply for ${currentTotalDays} day(s) — only ${selectedLeaveInfo?.remainingDays} remaining out of ${selectedLeaveInfo?.totalDays} day(s) for ${selectedLeaveInfo?.category?.name}`;
          severityType = "error";
        } else {
          alertText = `You have ${dropDownValue ? selectedLeaveInfo?.remainingDays : 0} remaining days available out of ${dropDownValue ? selectedLeaveInfo?.totalDays : 0}`;
          severityType = "success";
        }
        break;
    }
  } else {
    alertText = `Please Select A Category`
    severityType = "info"
  }

  const getMinDate = () => {
    return dropDownValue?.toLowerCase().includes('sick') ? dayjs().subtract(1, 'year') : isEmergency ? dayjs() : dayjs().add(15, 'day');
  };

  const getMaxDate = () => {
    return dropDownValue?.toLowerCase().includes('sick') ? dayjs() : dayjs().add(1, 'year').endOf('year');
  };

  const getEndDateMaxDate = () => {
    if (!startDate) return dayjs().add(1, 'year').endOf('year');
    
    // End date must be within the same year as start date
    const startYear = dayjs(startDate).year();
    const endOfStartYear = dayjs(`${startYear}-12-31`);
    const endOfMaxYear = dayjs().add(1, 'year').endOf('year');
    
    return endOfStartYear.isBefore(endOfMaxYear) ? endOfStartYear : endOfMaxYear;
  };

  return (
      <ContentContainer
          title={mode + ' Leave'}
          sx={{ textTransform: 'capitalize' }}
          AdornmentComponent={<Fragment />}
      >
          <GridContainer>
              <GridItem md={4} xs={12}>
                  <AutocompleteBase
                      name='category'
                      placeholder='Category Name'
                      label='Category Name'
                      required={true}
                      validate={[required]}
                      onChange={(_, value) => setDropDownValue(value)}
                      disabled={isLoadingCategories || availableCategories.length === 0}
                      options={availableCategories.map(cat => ({
                          label: cat.name,
                          value: cat.id,
                          ...cat
                      }))}
                  />
              </GridItem>
              <GridItem md={4} xs={12}>
                  <DatePickerBase
                      name='startDate'
                      placeholder='Start Date'
                      label='Start Date'
                      required={true}
                      validate={required}
                      disabled={dropDownValue ? false : true}
                      maxDate={getMaxDate()}
                      minDate={getMinDate()}
                      onChange={handleStartDateChange}
                  />
              </GridItem>
              <GridItem md={4} xs={12}>
                  <DatePickerBase
                      name='endDate'
                      placeholder='End Date'
                      label='End Date'
                      required={true}
                      validate={required}
                      maxDate={getEndDateMaxDate()}
                      disabled={startDate ? false : true}
                      minDate={dayjs(startDate)}
                      onChange={handleEndDateChange}
                  />
              </GridItem>
              <GridItem md={4} xs={12}>
                  <Input
                      name='totalNumberOfDays'
                      placeholder='Total Number of Days'
                      label='Total Number of Days'
                      required={true}
                      validate={[required]}
                      autoComplete='do-not-autofill'
                      type='number'
                      disabled
                  />
              </GridItem>
              <GridItem md={4} xs={12}>
                  <AutocompleteFetch
                      name='employeesToNotify'
                      placeholder='Emails'
                      label='Employees to Notify'
                      domain={COMMON_DOMAIN}
                      entity={GET_ALL_EMPLOYEE_NAME_EMAIL}
                      multiple
                      customselector={getAllEmployeeSelector}
                  />
              </GridItem>
              {inputFileHandler() && (
                  <GridItem md={4} xs={12}>
                      <InputFile
                          name='requiredDocuments'
                          label='Required Documents'
                      />
                  </GridItem>
              )}
              <GridItem md={12} xs={12}>
                  <InputRadio
                      name='leaveType'
                      label='Leave Type'
                      options={LEAVE_TYPE}
                      required={true}
                      validate={required}
                      onChange={(e, value) => {
                          setLeaveType(value);
                          setTotalNumberOfDays(startDate, endDate, value);
                      }}
                  />
              </GridItem>

              <GridItem md={12} xs={12}>
                  <TextArea
                      name='reason'
                      label='Reason'
                      rows='2'
                      required={true}
                      validate={[required]}
                  />
              </GridItem>
              <GridItem md={6} xs={12}>
                  <InputRadio
                      name='isEmergency'
                      label='Is Emergency?'
                      options={IS_EMERGENCY}
                      required={true}
                      value={isEmergency}
                      validate={required}
                      onChange={(e) => {
                          setIsEmergency(
                              e.target.value === 'true' ? true : false
                          );
                      }}
                  />
              </GridItem>

              {yearCrossError && (
                  <GridItem md={12} xs={12}>
                      <Alert severity="error">
                          Leave cannot span across different years. Please select start and end dates within the same year.
                      </Alert>
                  </GridItem>
              )}

              <GridItem md={12} sx={{ mt: 2 }} xs={12}>
                  <Alert severity={severityType}>{alertText}</Alert>
              </GridItem>

              <GridItem md={12} xs={12}>
                  <Divider sx={{ my: 3 }} />
                  <Typography 
                      variant="subtitle2" 
                      sx={{ 
                          mb: 2,
                          fontWeight: 600,
                          color: 'text.secondary'
                      }}
                  >
                      Leave Summary for {currentDisplayYear}
                  </Typography>
                  <StyledBox>
                      <TableContainer>
                          <TableHeaderContainer>
                              {Object.values(TABLE_DATA).map((th, i) => (
                                  <TableCellContainer
                                      sx={{
                                          pl: i === 0 ? 3 : 2,
                                          pr: 2,
                                          fontWeight: 600,
                                          textTransform: 'capitalize',
                                          backgroundColor: 'action.hover'
                                      }}
                                      typography='body2'
                                      key={th}
                                  >
                                      {th}
                                  </TableCellContainer>
                              ))}
                          </TableHeaderContainer>
                          <TableBody>
                              {data.length > 0 ? (
                                  data.map((row, i) => (
                                      <TableRow 
                                          key={i}
                                          sx={{
                                              '&:hover': {
                                                  backgroundColor: 'action.hover'
                                              }
                                          }}
                                      >
                                          {Object.keys(TABLE_DATA).map((key, index) => {
                                              switch (key) {
                                                  case 'category':
                                                      return (
                                                          <TableCellContainer
                                                              key={key}
                                                              sx={{
                                                                  pl: index === 0 ? 3 : 2,
                                                                  pr: 2,
                                                                  fontWeight: dropDownValue === row[key].name ? 600 : 400,
                                                                  color: dropDownValue === row[key].name ? 'primary.main' : 'text.primary'
                                                              }}
                                                          >
                                                              {row[key].name}
                                                          </TableCellContainer>
                                                      );
                                                  case 'remainingDays':
                                                      return (
                                                          <TableCellContainer
                                                              key={index}
                                                              sx={{
                                                                  pl: index === 0 ? 3 : 2,
                                                                  pr: 2,
                                                                  fontWeight: 500,
                                                                  color: row[key] === 0 || row[key] === -1 ? 'error.main' : 
                                                                         row[key] > 0 ? 'success.main' : 'text.primary'
                                                              }}
                                                          >
                                                              {row[key] === -1 ? 'N/A' : row[key]}
                                                          </TableCellContainer>
                                                      );
                                                  default:
                                                      return (
                                                          <TableCellContainer
                                                              key={index}
                                                              sx={{
                                                                  pl: index === 0 ? 3 : 2,
                                                                  pr: 2
                                                              }}
                                                          >
                                                              {row[key]}
                                                          </TableCellContainer>
                                                      );
                                              }
                                          })}
                                      </TableRow>
                                  ))
                              ) : (
                                  <TableRow>
                                      <TableCellContainer 
                                          colSpan={3} 
                                          sx={{ 
                                              textAlign: 'center', 
                                              py: 4,
                                              color: 'text.secondary'
                                          }}
                                      >
                                          No leave data available for {currentDisplayYear}
                                      </TableCellContainer>
                                  </TableRow>
                              )}
                          </TableBody>
                      </TableContainer>
                  </StyledBox>
              </GridItem>
          </GridContainer>
          <ButtonWrapper>
              <StyledButton
                  disabled={severityType !== 'success' || yearCrossError}
                  onClick={handleCreate}
              >
                  {mode === 'create' ? 'CREATE' : 'UPDATE'}
              </StyledButton>
          </ButtonWrapper>
      </ContentContainer>
  );
});

CreateLeave.propTypes = propTypes;
CreateLeave.defaultProps = defaultProps;

export { CreateLeave as default, CreateLeave };

